/**
 * One level of boolean logic over upstream answers — the model behind the rule editor.
 *
 * ## Why FLAT, and why that is a real restriction rather than a simplification
 *
 * A rule's condition is an arbitrary expression (see `ast.ts`). This module models only
 * the shape the editor can draw: a list of comparisons joined by a single AND or a
 * single OR, with no nesting. That covers the overwhelming majority of live conditions
 * and all the ones an author writes by hand.
 *
 * It does NOT cover everything the engine accepts, and the gap matters. `Q1.1.4.1`'s
 * eligibility rules add two fallback chains together; `Q1.1.7` calls `hasKey`; four
 * rules use `hasAnyMatch`. None of those is one level of boolean logic, and quietly
 * re-serialising one through this model would CHANGE it. So `toFlat` returns `null`
 * instead of an approximation, and the editor falls back to showing the expression —
 * refusing to edit is the only safe answer when the model cannot hold the data.
 *
 * ## Why the operator list is not just equal/greater/less
 *
 * The operator offered depends on what the referenced question is: a single-select has
 * one meaningful test (is it this answer), a multi-select has two (is it exactly this,
 * does it contain this), and a number has the comparisons. `>=` and `<=` are included
 * even though they are a longer list than one might design from scratch, because live
 * rules use them — leaving them out would make those edges unrepresentable here and
 * push them into the fallback for no reason.
 */
import type { QuestionnaireDefinition, QuestionnaireNode } from '../types';
import { type Expr, serialize, tryParse } from './ast';
import { type GraphFacts, type QuestionKind, questionKindOf } from './projection';

/** What a term can test, keyed by the kind of question it reads. */
export type TermOp = 'is' | 'contains' | '===' | '>=' | '>' | '<=' | '<';

export interface Term {
  /** The referenced question's `qualifiedNodeId` — what the expression names. */
  ref: string;
  op: TermOp;
  /** An option `value` for is/contains, a number for the comparisons. */
  value: string | number;
}

export interface FlatCondition {
  /** One join for the whole list: one level means one connective. */
  join: 'and' | 'or';
  terms: Term[];
}

/** Operators that make sense for each kind of question. */
export const OPS_FOR_KIND: Record<QuestionKind, TermOp[]> = {
  // One answer, so the only question is which one.
  single: ['is'],
  // `is` = the answer is exactly this one; `contains` = this one is among them.
  multi: ['is', 'contains'],
  number: ['===', '>', '>=', '<', '<='],
  // A free-text answer compared for equality. Rare, but `===` is well defined.
  text: ['is'],
  // A component writes whatever it writes; equality is all that is safe to assume.
  custom: ['is'],
};

export const OP_LABEL: Record<TermOp, string> = {
  is: '是',
  contains: '包含',
  '===': '等于',
  '>': '大于',
  '>=': '大于等于',
  '<': '小于',
  '<=': '小于等于',
};

/* ── Upstream variables ─────────────────────────────────────────────────────── */

export interface Variable {
  /** `qualifiedNodeId`, the name an expression uses. */
  ref: string;
  /** `nodeId`, for showing which card it is. */
  nodeId: string;
  label: string;
  kind: QuestionKind;
  options: { value: string; label: string }[];
}

/**
 * Questions whose answer is GUARANTEED to exist when this rule is evaluated.
 *
 * Dominators, not "every node drawn above this one". A question on a sibling branch is
 * higher up the canvas but is not answered on this path, so referencing it reads
 * `undefined` and the rule silently never fires — the exact defect the checker reports
 * as `ref-not-dominating`. Using `facts.dom` here means the editor cannot offer a
 * variable the checker would then flag; two implementations of "answered by now" would
 * eventually disagree and the surface would contradict itself.
 *
 * The source node itself is included: `dom` contains the node, and a rule leaving a
 * question is the normal place to test that question's own answer.
 */
export function upstreamVariables(
  def: QuestionnaireDefinition,
  facts: GraphFacts,
  sourceNodeId: string,
): Variable[] {
  const dom = facts.dom.get(sourceNodeId);
  if (!dom) return [];
  const out: Variable[] = [];
  for (const id of dom) {
    const n = facts.byNodeId.get(id);
    // Only a question with a `qualifiedNodeId` can be referenced at all — that is the
    // name expressions use, and a node missing it is a live defect the checker reports.
    if (!n?.qualifiedNodeId) continue;
    out.push(variableOf(n));
  }
  // Definition order, so the picker reads down the questionnaire rather than in
  // whatever order the dominator set happens to iterate.
  const rank = new Map(def.nodes.map((n, i) => [n.nodeId, i]));
  out.sort((a, b) => (rank.get(a.nodeId) ?? 0) - (rank.get(b.nodeId) ?? 0));
  return out;
}

export function variableOf(n: QuestionnaireNode): Variable {
  return {
    ref: n.qualifiedNodeId!,
    nodeId: n.nodeId,
    label: n.questionText.cn || n.questionText.en || n.nodeId,
    kind: questionKindOf(n),
    options: (n.answerOptions ?? []).map((o) => ({
      value: o.value,
      label: o.optionText.cn || o.optionText.en || o.value,
    })),
  };
}

/* ── Expression → flat model ────────────────────────────────────────────────── */

/**
 * One comparison, or `null` when this sub-expression is not one.
 *
 * `kindOf` decides whether `=== 'a'` reads as `is` on a choice question or as `===` on
 * a number, because the two look identical in the AST and are presented differently.
 */
function toTerm(e: Expr, kindOf: (ref: string) => QuestionKind | undefined): Term | null {
  if (e.k === 'has') {
    return e.x.k === 'var' ? { ref: e.x.ref, op: 'contains', value: e.v } : null;
  }
  if (e.k !== 'cmp') return null;
  // The variable may sit on either side; the live data always puts it on the left, and
  // a comparison with no variable at all is not something this editor can show.
  const flip: Record<TermOp, TermOp> = {
    is: 'is',
    contains: 'contains',
    '===': '===',
    '>': '<',
    '>=': '<=',
    '<': '>',
    '<=': '>=',
  };
  let ref: string;
  let op: TermOp = e.op;
  let lit: Expr;
  if (e.l.k === 'var') {
    ref = e.l.ref;
    lit = e.r;
  } else if (e.r.k === 'var') {
    ref = e.r.ref;
    lit = e.l;
    op = flip[e.op];
  } else return null;
  if (lit.k !== 'lit') return null;

  const kind = kindOf(ref);
  if (!kind) return null;
  // On a choice question `=== 'a'` IS the "is this answer" test; showing it as `===`
  // beside a number's `===` would suggest the two behave alike.
  if (op === '===' && kind !== 'number') op = 'is';
  if (!OPS_FOR_KIND[kind].includes(op)) return null;
  return { ref, op, value: lit.v };
}

/**
 * Read an expression as one level of boolean logic, or `null` if it is not.
 *
 * Deliberately strict. Everything that is not a flat join of recognised comparisons —
 * `not`, arithmetic, `coalesce`, `hasKey`, `hasAnyMatch`, a nested `and` inside an `or`
 * — returns `null` so the caller can show the expression instead of a lossy rendering
 * of it. An editor that round-trips a condition into something ALMOST the same is worse
 * than one that declines: the author would have no way to notice.
 */
export function toFlat(
  expression: string,
  def: QuestionnaireDefinition,
  facts: GraphFacts,
): FlatCondition | null {
  const e = tryParse(expression);
  if (!e) return null;
  const kindOf = (ref: string): QuestionKind | undefined => {
    const n = facts.byQualifiedId.get(ref);
    return n ? questionKindOf(n) : undefined;
  };
  void def;

  if (e.k === 'and' || e.k === 'or') {
    const terms: Term[] = [];
    for (const x of e.xs) {
      const t = toTerm(x, kindOf);
      if (!t) return null;
      terms.push(t);
    }
    return { join: e.k, terms };
  }
  const single = toTerm(e, kindOf);
  // A lone comparison is one level too. `and` is the join a second term would get.
  return single ? { join: 'and', terms: [single] } : null;
}

/* ── Flat model → expression ────────────────────────────────────────────────── */

function termToExpr(t: Term): Expr {
  if (t.op === 'contains') return { k: 'has', x: { k: 'var', ref: t.ref }, v: String(t.value) };
  return {
    k: 'cmp',
    // `is` is `===` in the language; it exists as a separate label because "是" reads
    // as a question about an answer while "等于" reads as arithmetic.
    op: t.op === 'is' ? '===' : t.op,
    l: { k: 'var', ref: t.ref },
    r: { k: 'lit', v: t.value },
  };
}

/**
 * Serialise through the AST rather than by string concatenation.
 *
 * The AST owns precedence, so an OR of comparisons comes out with the parentheses the
 * engine needs. Building the text by hand is how `Q1.1.4.1 program#3` lost its
 * parentheses and turned a sum into a truthiness chain — a live defect, from exactly
 * this operation done by eye.
 */
export function fromFlat(flat: FlatCondition): string {
  if (flat.terms.length === 0) return '';
  if (flat.terms.length === 1) return serialize(termToExpr(flat.terms[0]));
  return serialize({ k: flat.join, xs: flat.terms.map(termToExpr) });
}

/** A term the UI can start editing: first available operator, no value chosen yet. */
export function freshTerm(v: Variable): Term {
  const op = OPS_FOR_KIND[v.kind][0];
  return {
    ref: v.ref,
    op,
    value: v.kind === 'number' ? 0 : (v.options[0]?.value ?? ''),
  };
}

/** Is every term filled in enough to serialise? */
export function flatIsComplete(flat: FlatCondition): boolean {
  return (
    flat.terms.length > 0 &&
    flat.terms.every((t) => t.ref !== '' && (typeof t.value === 'number' || t.value !== ''))
  );
}
